/**
 * The offer to install the app to the home screen.
 *
 * This matters more here than in most web apps. A sit is twenty-five minutes with the
 * screen off; in a browser tab that means a URL bar, a tab strip, and a back-swipe that
 * can throw the session away mid-silence. Installed, it is a standalone app with its own
 * icon, and the service worker already makes it work with no network.
 *
 * Three platforms, three truths:
 *
 *   * Chromium fires `beforeinstallprompt`, which we capture and replay on a tap. This
 *     is the only path that can actually install without instructions.
 *   * iOS Safari never fires it and exposes no install API at all, so the honest thing
 *     is to describe the Share -> Add to Home Screen route in words. Getting this wrong
 *     — showing an Install button that does nothing — is worse than showing nothing.
 *   * Anything already standalone must show none of it.
 *
 * The catching and the state live in state/install.ts, because by the time this
 * component renders the event has usually already fired. The banner is a single row on
 * the home screen, which has no space to spare, and the dismissal is time-boxed rather
 * than permanent — a permanent one is how the offer went missing in the first place.
 */

import {
  dismissOffer, installExplanation, installState, offerVisible, promptToInstall,
} from '../state/install';
import { nowMs } from '../state/timing';

/**
 * The home-screen banner. Renders nothing unless there is something real to offer.
 */
export function InstallOffer() {
  // Reading the clock keeps a dismissal that has aged out from needing a reload, and
  // costs nothing — the home screen already re-renders every second for the finish time.
  const now = nowMs.value;
  if (!offerVisible(now)) return null;
  const state = installState(now);

  return (
    <aside class="install-row" aria-labelledby="install-h">
      <span id="install-h" class="install-copy">
        {state.kind === 'ios'
          ? 'Add to Home Screen from the Share menu — it opens without a browser.'
          : 'Keep it on your home screen. It opens without a browser and works offline.'}
      </span>
      {state.kind === 'prompt' && (
        <button class="btn install-btn" onClick={() => void promptToInstall()}>
          Install
        </button>
      )}
      <button class="btn btn-quiet install-x" aria-label="Dismiss the install suggestion"
              onClick={() => dismissOffer()}>
        ✕
      </button>
    </aside>
  );
}

/**
 * The Settings entry. Unlike the banner this always says something, including once the
 * app is installed — otherwise dismissing the banner leaves no trace of the feature.
 *
 * It also always says *why*, because four different reasons for showing no Install
 * button used to render identically as nothing at all, and "it does not offer to
 * install" was a report nobody could act on.
 */
export function InstallSetting() {
  const now = nowMs.value;
  const state = installState(now);

  return (
    <div class="stack-sm">
      {state.kind === 'prompt' && (
        <button class="btn" style={{ alignSelf: 'flex-start' }}
                onClick={() => void promptToInstall()}>
          Install to home screen
        </button>
      )}
      <p class="dim" style={{ fontSize: 'var(--step--1)' }} data-testid="install-state">
        {installExplanation(state, now)}
      </p>
    </div>
  );
}
